import type { Item } from '@eshop/types/catalogue.types.ts';

export interface ResolveCanBuyParams {
  erpEnabled: boolean;
  blockSalesWithoutStock: boolean;
  item: Pick<Item, 'stock' | 'stock_state' | 'can_buy' | 'disponibilidad'>;
}

export interface ResolveCanBuyResult {
  policyEnforced: boolean;
  allowBackorderPurchases: boolean;
  canBuy: boolean;
  isBlockedByStock: boolean;
  stockState: Item['stock_state'] | null | undefined;
}

const hasAvailableStock = (stock: Item['stock']): boolean | undefined => {
  if (typeof stock !== 'number') {
    return undefined;
  }

  if (Number.isNaN(stock)) {
    return undefined;
  }

  return stock > 0;
};

export const resolveCanBuy = ({
  erpEnabled,
  blockSalesWithoutStock,
  item,
}: ResolveCanBuyParams): ResolveCanBuyResult => {
  const policyEnforced = Boolean(erpEnabled && blockSalesWithoutStock);
  const allowBackorderPurchases = !policyEnforced;
  const availableStock = hasAvailableStock(item.stock ?? null);
  const stockState = item.stock_state ?? null;

  const isBlockedByStock = Boolean(
    policyEnforced &&
      ((availableStock !== undefined && !availableStock) || stockState === 'out_of_stock'),
  );

  const availabilitySource =
    typeof item.can_buy === 'boolean' ? item.can_buy : item.disponibilidad !== false;

  let canBuy = availabilitySource;

  if (isBlockedByStock) {
    canBuy = false;
  } else if (!policyEnforced && stockState === 'backorder') {
    canBuy = true;
  }

  return {
    policyEnforced,
    allowBackorderPurchases,
    canBuy,
    isBlockedByStock,
    stockState,
  } satisfies ResolveCanBuyResult;
};

export default resolveCanBuy;
